import React, { useState, useEffect } from 'react';
import { FiSearch, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const StaffPembayaran = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  // Mock data for development - replace with API call in production
  useEffect(() => {
    // Fetch payment data from API
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const response = await API.get('/api/booking/payments/pending');
        console.log('Fetched payment data:', response.data);
        setPayments(response.data);
        setTotalPages(Math.ceil(response.data.length / 10));
      } catch (error) {
        console.error('Error fetching payments:', error);
        setPayments([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [currentPage, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // Trigger search through useEffect
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getServiceCount = (payment) => payment?.service_items?.length || 0;

  const getBatchBadge = (payment) => {
    const serviceCount = getServiceCount(payment);

    if (serviceCount <= 1) {
      return null;
    }

    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
        Batch {serviceCount} layanan
      </span>
    );
  };

  const handleViewDetail = (payment) => {
    setSelectedPayment(payment);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedPayment(null);
  };

  const handlePaymentAction = (action, payment) => {
    setSelectedPayment(payment);
    setConfirmAction(action);
    setConfirmModalOpen(true);
  };

  const confirmPaymentAction = async () => {
    try {
      setLoading(true);
      const endpoint = confirmAction === 'approve'
        ? `/api/booking/payments/${selectedPayment.id}/approve`
        : `/api/booking/payments/${selectedPayment.id}/reject`;

      console.log(`Sending request to ${endpoint} with data:`, {
        admin_notes: selectedPayment.admin_notes || ''
      });

      const response = await API.post(endpoint, {
        admin_notes: selectedPayment.admin_notes || ''
      });

      console.log(`Payment ${confirmAction} response:`, response.data);

      // Update local state to reflect the change
      setPayments(payments.filter(p => p.id !== selectedPayment.id));

      // Close modals
      setConfirmModalOpen(false);
      setShowDetailModal(false);

      // Show success message
      alert(`Pembayaran berhasil ${confirmAction === 'approve' ? 'disetujui' : 'ditolak'}`);

      // Set redirecting state to true to show loading indicator
      setRedirecting(true);

      // Redirect to transaction history page after a short delay
      setTimeout(() => {
        window.location.href = '/staff/riwayat-transaksi';
      }, 1000); // 1 second delay
    } catch (error) {
      console.error(`Error ${confirmAction} payment:`, error);

      // Show detailed error message
      let errorMessage = 'Terjadi kesalahan. ';

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        errorMessage += error.response.data?.message || `Status: ${error.response.status}`;
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage += 'Tidak ada respons dari server. Periksa koneksi jaringan Anda.';
        console.error('Error request:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage += error.message;
        console.error('Error message:', error.message);
      }

      // Show error in alert
      alert(`Gagal ${confirmAction === 'approve' ? 'menyetujui' : 'menolak'} pembayaran. ${errorMessage}`);

      // Keep modals open so user can try again
      setConfirmModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const renderPagination = () => {
    return (
      <div className="flex justify-center mt-6">
        <nav className="flex items-center">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Sebelumnya
          </button>
          <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-sm text-gray-700">
            Halaman {currentPage} dari {totalPages}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Berikutnya
          </button>
        </nav>
      </div>
    );
  };

  const renderDetailModal = () => {
    if (!selectedPayment) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">Detail Pembayaran</h3>
            <button
              onClick={closeDetailModal}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4">
            {/* Header section */}
            <div className="mb-4 flex justify-between">
              <div>
                <div className="text-sm text-gray-500">Invoice</div>
                <div className="font-semibold">{selectedPayment.invoice_number}</div>
                <div className="mt-2">{getBatchBadge(selectedPayment)}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total</div>
                <div className="font-semibold text-lg">{formatRupiah(selectedPayment.total_amount)}</div>
              </div>
            </div>

            {/* Down Payment Alert */}
            {selectedPayment.is_down_payment && (
              <div className="mb-4 border-l-4 border-yellow-400 bg-yellow-50 p-4 rounded-md">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-bold text-yellow-700">Pembayaran DP</p>
                    <p className="text-sm text-yellow-600">
                      Customer membayar DP sebesar {selectedPayment.down_payment_percent}% ({formatRupiah(selectedPayment.down_payment_amount)})
                      dari total {formatRupiah(selectedPayment.subtotal)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Customer & Vehicle info */}
            <div className="mb-4 border rounded-md p-3 bg-gray-50">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Pelanggan</div>
                  <div className="font-medium">{selectedPayment.customer_name}</div>
                  <div className="text-sm text-gray-600">{selectedPayment.customer_phone}</div>
                  <div className="text-sm text-gray-600">{selectedPayment.customer_email}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Kendaraan</div>
                  <div className="font-medium">{selectedPayment.vehicle_make} {selectedPayment.vehicle_model} ({selectedPayment.vehicle_year})</div>
                  <div className="text-sm text-gray-600">Plat: {selectedPayment.vehicle_plate}</div>
                </div>
              </div>
            </div>

            {/* Date & Service Info */}
            <div className="mb-4 border rounded-md p-3 bg-gray-50">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Tanggal Servis</div>
                  <div className="font-medium">{selectedPayment.service_date}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Tanggal Pembayaran</div>
                  <div className="font-medium">{selectedPayment.payment_date}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Jumlah Layanan</div>
                  <div className="font-medium">{getServiceCount(selectedPayment)} layanan</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Batch ID</div>
                  <div className="font-medium">{selectedPayment.booking_batch_id || '-'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-gray-500 mb-1">Montir</div>
                  <div className="font-medium">{selectedPayment.mechanic_name || 'Belum ditugaskan'}</div>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <div className="text-sm text-gray-500 mb-2">Item Servis & Sparepart</div>
              <div className="border rounded overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Harga
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedPayment.service_items.map((item, index) => (
                      <tr key={index} className={item.type === 'other' ? 'bg-gray-50' : ''}>
                        <td className="px-3 py-2 text-sm text-gray-900">
                          {item.name}
                          {item.type === 'service' && <span className="ml-1 text-xs text-blue-600">(Jasa)</span>}
                          {item.type === 'part' && <span className="ml-1 text-xs text-green-600">(Part)</span>}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-500 text-center">
                          {item.qty}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 text-right">
                          {formatRupiah(item.price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td colSpan="2" className="px-3 py-2 text-xs font-medium text-gray-500">
                        Subtotal
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                        {formatRupiah(selectedPayment.subtotal)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" className="px-3 py-2 text-xs font-medium text-gray-500">
                        Pajak (PPN 10%)
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                        {formatRupiah(selectedPayment.tax)}
                      </td>
                    </tr>
                    <tr className="bg-gray-200">
                      <td colSpan="2" className="px-3 py-2 text-sm font-bold text-gray-700">
                        Total
                      </td>
                      <td className="px-3 py-2 text-sm font-bold text-gray-900 text-right">
                        {formatRupiah(selectedPayment.total_amount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mb-4 border rounded-md p-3 bg-gray-50">
              <div className="text-sm text-gray-500 mb-1">Metode Pembayaran</div>
              <div className="font-medium flex items-center">
                {selectedPayment.payment_method}
                {selectedPayment.is_down_payment && (
                  <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded">Down Payment</span>
                )}
                {!selectedPayment.is_down_payment && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">Pembayaran Penuh</span>
                )}
              </div>

              {selectedPayment.is_down_payment && (
                <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                  <div className="text-sm font-medium text-yellow-800">Detail Down Payment:</div>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <span className="text-xs text-yellow-700">Persentase DP:</span>
                      <div className="font-medium">{selectedPayment.down_payment_percent}%</div>
                    </div>
                    <div>
                      <span className="text-xs text-yellow-700">Jumlah DP:</span>
                      <div className="font-medium">{formatRupiah(selectedPayment.down_payment_amount)}</div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs text-yellow-700">Sisa Pembayaran:</span>
                      <div className="font-medium">{formatRupiah(selectedPayment.subtotal - selectedPayment.down_payment_amount)}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-2 text-sm">
                <span className="text-gray-500">No. Rekening:</span> {selectedPayment.payment_account}
              </div>
              <div className="text-sm">
                <span className="text-gray-500">Atas Nama:</span> {selectedPayment.payment_account_name}
              </div>
              {selectedPayment.payment_notes && (
                <div className="mt-2 text-sm text-gray-500">
                  <span className="font-medium">Catatan:</span> {selectedPayment.payment_notes}
                </div>
              )}
            </div>

            {/* Payment Proof */}
            <div className="mb-4 border rounded-md p-3 bg-gray-50">
              <div className="text-sm text-gray-500 mb-1">Bukti Pembayaran:</div>
              <div className="flex items-center mt-2">
                <div className="bg-gray-200 p-2 rounded mr-2">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-2">
                  <div className="text-sm font-medium">{selectedPayment.payment_proof_name}</div>
                  <div className="flex space-x-2 mt-1">
                    <a
                      href={selectedPayment.payment_proof_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Lihat Gambar
                    </a>
                    <a
                      href={selectedPayment.payment_proof_url}
                      download
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Download
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Notes */}
            <div className="mb-4">
              <div>
                <p className="text-sm text-gray-600">Catatan Admin</p>
                <textarea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  rows="2"
                  placeholder="Tambahkan catatan admin di sini..."
                  value={selectedPayment.admin_notes || ''}
                  onChange={(e) => setSelectedPayment({ ...selectedPayment, admin_notes: e.target.value })}
                ></textarea>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => handlePaymentAction('reject', selectedPayment)}
                className="px-4 py-2 bg-white border border-red-500 text-red-500 rounded-md hover:bg-red-50 flex items-center"
              >
                <FiXCircle className="mr-1" /> Tolak Pembayaran
              </button>
              <button
                onClick={() => handlePaymentAction('approve', selectedPayment)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              >
                <FiCheckCircle className="mr-1" /> Terima Pembayaran
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmModal = () => {
    if (!confirmAction || !selectedPayment) return null;

    const isApprove = confirmAction === 'approve';
    const isDownPayment = selectedPayment.is_down_payment;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            {isApprove ? 'Konfirmasi Penerimaan Pembayaran' : 'Konfirmasi Penolakan Pembayaran'}
          </h3>

          <p className="text-gray-600 mb-4">
            {isApprove
              ? `Apakah Anda yakin ingin menerima pembayaran ini${isDownPayment ? ' (Down Payment)' : ''}? Tindakan ini akan memperbarui status pembayaran menjadi "diterima".`
              : `Apakah Anda yakin ingin menolak pembayaran ini${isDownPayment ? ' (Down Payment)' : ''}? Tindakan ini akan memperbarui status pembayaran menjadi "ditolak".`
            }
          </p>

          {isDownPayment && isApprove && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm font-medium text-yellow-800">Informasi Down Payment:</p>
              <ul className="mt-1 text-sm text-yellow-700">
                <li>• Persentase DP: {selectedPayment.down_payment_percent}%</li>
                <li>• Jumlah DP: {formatRupiah(selectedPayment.down_payment_amount)}</li>
                <li>• Total Layanan: {formatRupiah(selectedPayment.subtotal)}</li>
                <li>• Sisa Pembayaran: {formatRupiah(selectedPayment.subtotal - selectedPayment.down_payment_amount)}</li>
              </ul>
              <p className="mt-2 text-xs text-yellow-600">
                Setelah menyetujui pembayaran DP, status booking akan diubah menjadi "confirmed".
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setConfirmModalOpen(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Batal
            </button>
            <button
              onClick={confirmPaymentAction}
              className={`px-4 py-2 ${isApprove ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
                } text-white rounded-md`}
            >
              {isApprove ? 'Terima' : 'Tolak'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout userRole="staff">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Konfirmasi Pembayaran</h1>
        <p className="text-gray-600">Verifikasi pembayaran pelanggan yang menunggu konfirmasi</p>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Cari ID pembayaran atau nama pelanggan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Cari
          </button>
        </form>
      </div>

      {/* Payment List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-20 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600"></div>
            <p className="mt-2 text-gray-500">Memuat data...</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-gray-500">Tidak ada pembayaran yang menunggu konfirmasi</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID Invoice
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pelanggan
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kendaraan
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tanggal
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Jumlah
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{payment.invoice_number}</div>
                      {getBatchBadge(payment) && (
                        <div className="mt-1">{getBatchBadge(payment)}</div>
                      )}
                      <div className="text-xs text-gray-500">#{payment.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{payment.customer_name}</div>
                      <div className="text-xs text-gray-500">{payment.customer_phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payment.vehicle_make} {payment.vehicle_model}</div>
                      <div className="text-xs text-gray-500">{payment.vehicle_plate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{payment.service_date}</div>
                      <div className="text-xs text-gray-400">Pembayaran: {payment.payment_date}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.is_down_payment ? (
                        <div>
                          <div className="flex items-center">
                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded mr-1">DP</span>
                            {formatRupiah(payment.down_payment_amount)}
                          </div>
                          <div className="text-xs text-gray-500">dari {formatRupiah(payment.subtotal)}</div>
                        </div>
                      ) : (
                        <div>
                          <div>{formatRupiah(payment.total_amount)}</div>
                          <div className="text-xs font-normal text-gray-500">
                            {getServiceCount(payment)} layanan
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleViewDetail(payment)}
                        className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full font-medium text-sm transition"
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && payments.length > 0 && renderPagination()}

      {/* Detail Modal */}
      {showDetailModal && renderDetailModal()}

      {/* Confirmation Modal */}
      {confirmModalOpen && renderConfirmModal()}

      {/* Redirecting Overlay */}
      {redirecting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto"></div>
            <p className="mt-4 text-lg font-medium text-gray-800">Mengalihkan ke halaman transaksi...</p>
            <p className="mt-2 text-gray-600">Mohon tunggu sebentar</p>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default StaffPembayaran; 
